<script setup lang="ts">
import { computed } from 'vue';
import { isNil } from 'lodash';

import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { useCarrierFetcher } from '@satellite/composables/useCarrierFetcher';
import { useCarrierDisplay } from '@satellite/composables/useCarrierDisplay';
import CarrierDisplay from '@satellite/components/elements/presenters/CarrierDisplay.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import {
  mapCompanyDisplayFieldsToCarrier,
  type CompanyDisplayField
} from '@satellite/composables/useCompanyDisplay';
import type {
  CarrierCompanyDetails,
  CarrierUserDetails
} from '@satellite/composables/carrierPresenterShared';

const {
  size = 'regular',
  initialCompanyData = null,
  placeholderCompanyData = null,
  initialCarrierData = null,
  placeholderCarrierData = null,
  staticCarrierData = null,
  staticCompanyData = null,
  companyId = null,
  carrierId = null,
  mode = EntityPresenterMode.Live,
  displayFields = ['name'],
  nameProps = {},
  scacProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  initialCompanyData?: Maybe<CarrierCompanyDetails>;
  placeholderCompanyData?: Maybe<PresenterPlaceholderData<CarrierCompanyDetails>>;
  initialCarrierData?: Maybe<CarrierUserDetails>;
  placeholderCarrierData?: Maybe<PresenterPlaceholderData<CarrierUserDetails>>;
  staticCarrierData?: Maybe<CarrierUserDetails>;
  staticCompanyData?: Maybe<CarrierCompanyDetails>;
  companyId?: Maybe<string>;
  carrierId?: Maybe<string>;
  mode?: EntityPresenterMode;
  displayFields?: CompanyDisplayField[];
  nameProps?: Record<string, unknown>;
  scacProps?: Record<string, unknown>;
}>();

const resolvedStaticCarrierData = computed((): CarrierUserDetails | null => {
  if (!isNil(staticCarrierData)) {
    return staticCarrierData;
  }

  if (!isNil(staticCompanyData)) {
    return {
      id: staticCompanyData.id,
      company: staticCompanyData
    };
  }

  return null;
});

const {
  data: carrier,
  isLoading,
  isFetching,
  onIntersect
} = useCarrierFetcher({
  companyId: computed(() => companyId),
  carrierId: computed(() => carrierId),
  initialCarrierData: computed(() => initialCarrierData),
  placeholderCarrierData: computed(() => placeholderCarrierData),
  initialCompanyData: computed(() => initialCompanyData),
  placeholderCompanyData: computed(() => placeholderCompanyData),
  staticCarrierData: resolvedStaticCarrierData,
  mode
});

const data = useCarrierDisplay(carrier, {
  size: computed(() => size),
  mode: computed(() => mode),
  isFetching,
  companyNameProps: computed(() => nameProps),
  companyScacProps: computed(() => scacProps)
});

const resolvedDisplayFields = computed(() => mapCompanyDisplayFieldsToCarrier(displayFields));
</script>

<template>
  <div
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.25]
      }
    }">
    <v-skeleton-loader :loading="isLoading" type="text" class="company-presenter-v2 bg-transparent">
      <carrier-display
        :data="data"
        :display-fields="resolvedDisplayFields"
        column-gap-class="gap-1"
        :style="isLoading ? 'min-width: 50px' : ''">
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
          <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
      </carrier-display>
    </v-skeleton-loader>
  </div>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
